import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject, of, timer } from 'rxjs';
import { catchError, finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { ClientDeactivationRun } from 'src/app/models/client-deactivation.model';
import { Client, ClientDeactivationStamp, ClientReactivationStamp } from 'src/app/models/client.model';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { DialogService } from 'src/app/services/dialog.service';
import { SnackService } from 'src/app/services/snack.service';

/**
 * How often the run is polled while it is going.
 *
 * A panel of 3000 takes a couple of minutes, so three seconds keeps the bar visibly moving without
 * making the poll itself the load. The endpoint's own throttle allows 120/minute per caller, well
 * clear of this.
 */
const POLL_INTERVAL_MS = 3000;

/**
 * Consecutive failed polls before the card stops asking and says so.
 *
 * Keeping the last known progress is right for a blip, but without a floor a backend that is down —
 * or a token that expires mid-run — leaves the card polling every three seconds for the lifetime of
 * the page while the label keeps asserting "Unenrolling patients — 64 of 128". That is
 * indistinguishable from a slow run, which is the one thing this card must never be.
 *
 * Five at three seconds is fifteen seconds of silence: past any single blip, well short of a wait
 * that teaches an administrator to ignore the number.
 */
const MAX_POLL_FAILURES = 5;

/**
 * A stamp timestamp as a `Date` the `date` pipe can render: a Firestore `Timestamp` when read through
 * AngularFire, an ISO string when mirrored from an API response, or already a `Date`.
 */
/** Same reading as the `clientId` getter, for a client that is not `this.client`. */
const idOf = (client: Client | undefined | null): string | undefined => client?.client_id ?? client?.id;

const toDate = (value: any): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return typeof value.toDate === 'function' ? value.toDate() : null;
};

@Component({
  selector: 'app-client-deactivation',
  templateUrl: './client-deactivation.component.html',
  styleUrls: ['./client-deactivation.component.scss'],
})
export class ClientDeactivationComponent implements OnChanges, OnDestroy {
  @Input() client: Client;
  /** The parent holds the client object it passed in, so it is told to refresh rather than left stale. */
  @Output() clientDeactivated = new EventEmitter<string>();
  /** Same reason as `clientDeactivated`: the parent re-reads the client instead of keeping a stale copy. */
  @Output() clientReactivated = new EventEmitter<string>();

  isCountingPatients = false;
  isStarting = false;
  isReactivating = false;
  run: ClientDeactivationRun | null = null;
  /** The run is still going on the server, but this screen has stopped being able to see it. */
  pollUnavailable = false;

  private destroy$ = new Subject<void>();
  /** Separate from `destroy$` so switching client stops one poll without tearing the component down. */
  private stopPolling$ = new Subject<void>();
  private pollFailures = 0;

  constructor(
    private clientServicesService: ClientServicesService,
    private dialogService: DialogService,
    private snackBarService: SnackService
  ) {}

  /**
   * `active` is optional on `Client` and missing on older client documents, which are live clients.
   * Only an explicit `false` means deactivated.
   */
  get isActive(): boolean {
    return this.client?.active !== false;
  }

  get clientId(): string {
    return this.client?.client_id ?? this.client?.id;
  }

  get isRunning(): boolean {
    return this.run?.status === 'running';
  }

  get isBusy(): boolean {
    return this.isCountingPatients || this.isStarting || this.isRunning || this.isReactivating;
  }

  /** Zero while the run is opening and its denominator is not known yet. */
  get progressPercent(): number {
    if (!this.run?.total) {
      return 0;
    }
    return Math.min(100, Math.round((this.run.patientsProcessed / this.run.total) * 100));
  }

  /** A failed run leaves the client active on purpose, so the card has to offer the retry. */
  get hasFailedRun(): boolean {
    return this.run?.status === 'failed';
  }

  get stamp(): ClientDeactivationStamp | undefined {
    return this.client?.deactivation;
  }

  /**
   * The stamp's timestamp as a `Date` the `date` pipe can render.
   *
   * The client document is read through AngularFire, so this arrives as a Firestore `Timestamp`, not
   * a `Date` — the pipe renders a raw Timestamp as an empty string rather than failing, which is the
   * kind of miss nobody notices in review.
   */
  get deactivatedAt(): Date | null {
    return toDate(this.stamp?.deactivated_at);
  }

  get reactivationStamp(): ClientReactivationStamp | undefined {
    return this.client?.reactivation;
  }

  /** Same conversion as `deactivatedAt`, plus the ISO string the card mirrors right after reactivating. */
  get reactivatedAt(): Date | null {
    return toDate(this.reactivationStamp?.reactivated_at);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only a different client resets the card. The dashboard watches the selected client live, so
    // the same client arrives again as a fresh object on every write to its document — including
    // the run's own final `active: false`. Resetting on those would stop the poll before it sees the
    // run finish, and drop the in-flight flags of a request that has not answered yet.
    if (changes.client && idOf(changes.client.previousValue) !== idOf(changes.client.currentValue)) {
      this.stopPolling$.next();
      this.isCountingPatients = false;
      this.isStarting = false;
      this.isReactivating = false;
      this.run = null;
      this.pollUnavailable = false;
      this.pollFailures = 0;
      this.loadExistingRun();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * The count is resolved before the dialog opens, never inside it: the whole point of the
   * confirmation is to state how many patients this affects, so a failed count must stop the flow
   * instead of showing an unknown number.
   *
   * The backend counts the panel again when it opens the run, and that second figure is what the
   * progress bar divides by. Both calls are necessary: the administrator has to see the number
   * BEFORE confirming, and the run does not exist until after.
   */
  confirmDeactivation(): void {
    if (!this.clientId || !this.isActive || this.isBusy) {
      return;
    }
    const client = this.client;
    const clientId = this.clientId;
    this.isCountingPatients = true;
    this.clientServicesService
      .getClientDeactivationImpact(clientId)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.isStillSelected(clientId)) {
            this.isCountingPatients = false;
          }
        })
      )
      .subscribe(
        (impact) => {
          // A count that lands after the administrator moved to another client belongs to the old
          // one. Opening the dialog then would pair that number with the new client's name.
          if (this.isStillSelected(clientId)) {
            this.openConfirmationDialog(client, impact.activePatients);
          }
        },
        (error) => {
          console.error(error);
          this.snackBarService.genericSnackBar(
            'Could not read the patient count for this client, so the deactivation was not started.',
            ['error-snackbar'],
            5000
          );
        }
      );
  }

  /**
   * Makes a deactivated client active again. Its patients are NOT re-enrolled.
   *
   * Only the client flag moves. The deactivation run turned every patient's programs off and marked
   * them `Inactive - Client Terminated`, and none of that is reversed here — re-enrolment is per
   * patient, on the chart, and the confirmation says so before the administrator agrees.
   *
   * Goes through `POST /clients/:id/reactivation`, which re-asserts `isAdmin` and writes a
   * `reactivation` stamp in the same update as the flag. The `deactivation` stamp is left in place on
   * purpose — the termination still happened — and `active` is what says whether the client is live now.
   */
  confirmReactivation(): void {
    if (!this.clientId || this.isActive || this.isBusy) {
      return;
    }
    const client = this.client;
    const clientName = client?.client_name;
    this.dialogService
      .openReactivateClientDialog(clientName)
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.confirmed) {
          this.reactivateClient(client, clientName);
        }
      });
  }

  /**
   * Whether the card is still showing the client a request was made for.
   *
   * Compared by id, not by object: the dashboard hands in a fresh object for the same client when it
   * re-reads it, and that is still the same client.
   */
  private isStillSelected(clientId: string): boolean {
    return !!clientId && this.clientId === clientId;
  }

  /**
   * Picks up a run that is already going for this client.
   *
   * An administrator who reloads the page mid-run has lost the job id, and without this the card
   * would offer to start a second deactivation that the backend would only refuse with a 409.
   */
  private loadExistingRun(): void {
    if (!this.clientId) {
      return;
    }
    this.clientServicesService
      .getClientDeactivationRun(this.clientId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(
        (run) => {
          this.run = run;
          if (run?.status === 'running') {
            this.pollUntilFinished();
          }
        },
        // Not surfaced: this runs on every client selection, and a card that cannot read history is
        // still a card that can start a deactivation. The start call reports its own failures.
        (error) => console.error(error)
      );
  }

  private openConfirmationDialog(client: Client, activePatients: number): void {
    this.dialogService
      .openDeactivateClientDialog(client?.client_name, activePatients)
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.confirmed) {
          this.startDeactivation(client);
        }
      });
  }

  /**
   * Everything the response touches is taken from `client`, captured when the request went out,
   * never from `this.client` at response time: the administrator can pick another client while the
   * call is in flight. See `isStillSelected`.
   */
  private startDeactivation(client: Client): void {
    const clientId = client?.client_id ?? client?.id;
    this.isStarting = true;
    this.clientServicesService
      .startClientDeactivation(clientId)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.isStillSelected(clientId)) {
            this.isStarting = false;
          }
        })
      )
      .subscribe(
        (run) => {
          // The run started on the server either way, so the administrator is always told. The card
          // only adopts it, and polls it, if it is still showing that client; otherwise the other
          // client's card would render this run's progress. Selecting the client again picks the
          // run up through `loadExistingRun`.
          this.snackBarService.genericSnackBar(
            `Deactivating ${run.clientName} — ${run.total} patient${run.total === 1 ? '' : 's'} to unenroll (${run.reference}).`,
            ['success-snackbar'],
            5000
          );
          if (this.isStillSelected(clientId)) {
            this.run = run;
            this.pollUntilFinished();
          }
        },
        (error) => {
          console.error(error);
          // The backend states exactly why it refused — already deactivated, a run already in
          // flight, not an administrator — and each is something the user can act on, so its message
          // is worth more here than a generic one.
          this.snackBarService.genericSnackBar(
            error?.error?.message ?? 'An error occurred while starting the deactivation.',
            ['error-snackbar'],
            6000
          );
          // A 409 means the card is behind the server: the client is already deactivated, or a run is
          // already going. The parent's re-read brings the deactivated state; the run is picked up
          // here, since a re-read of the same client no longer resets the card (see `ngOnChanges`).
          // Otherwise the button stays offered and every retry gets the same 409.
          if (error?.status === 409 && this.isStillSelected(clientId)) {
            this.clientDeactivated.emit(clientId);
            this.loadExistingRun();
          }
        }
      );
  }

  private pollUntilFinished(): void {
    this.stopPolling$.next();
    this.pollFailures = 0;
    this.pollUnavailable = false;
    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.clientServicesService.getClientDeactivationRun(this.clientId).pipe(
            // A failed poll is not a failed run. Keeping the last known progress and trying again
            // means one blip does not freeze the bar for the rest of the run.
            //
            // But only up to a point. Without the ceiling, a backend that is down or a token that
            // expired mid-run leaves this asking every three seconds forever while the label above
            // keeps asserting a figure it can no longer confirm — and an administrator watching that
            // cannot tell it apart from a slow run. Past the ceiling the card stops and says so; the
            // run itself is untouched and continues on the server.
            catchError((error) => {
              console.error(error);
              this.pollFailures += 1;
              if (this.pollFailures >= MAX_POLL_FAILURES) {
                this.pollUnavailable = true;
                this.stopPolling$.next();
              }
              // `null`, not the last known run: the subscriber below skips a null and so leaves both
              // `this.run` and the failure count alone. Re-emitting the last run instead would reach
              // the success path and reset the counter, and the ceiling could never be hit.
              return of(null);
            })
          )
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$)
      )
      .subscribe((run) => {
        if (!run) {
          return;
        }
        // Consecutive, not cumulative: a run that recovers has not been failing.
        this.pollFailures = 0;
        this.run = run;
        if (run.status !== 'running') {
          this.stopPolling$.next();
          this.onRunFinished(run);
        }
      });
  }

  private onRunFinished(run: ClientDeactivationRun): void {
    if (run.status === 'completed') {
      // The dashboard keeps its selected client in memory, so the flag is mirrored locally to keep
      // this card in step with what the run just wrote.
      if (this.client) {
        this.client.active = false;
      }
      this.snackBarService.genericSnackBar(
        `${run.clientName} has been deactivated — ${run.patientsProcessed} patient${
          run.patientsProcessed === 1 ? '' : 's'
        } unenrolled.`,
        ['success-snackbar'],
        6000
      );
      this.clientDeactivated.emit(this.clientId);
      return;
    }

    this.snackBarService.genericSnackBar(
      run.failureReason ?? `The deactivation of ${run.clientName} did not finish. The client is still active.`,
      ['error-snackbar'],
      8000
    );
  }

  /** Same capture rule as `startDeactivation`: nothing here reads `this.client` at response time. */
  private reactivateClient(client: Client, clientName: string): void {
    const clientId = client?.client_id ?? client?.id;
    this.isReactivating = true;
    this.clientServicesService
      .reactivateClient(clientId)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.isStillSelected(clientId)) {
            this.isReactivating = false;
          }
        })
      )
      .subscribe(
        (result) => {
          // Mirrored on the object that was reactivated, for the same reason as on completion of a
          // deactivation: the dashboard keeps it in memory and re-reads it only when told to.
          if (client) {
            client.active = true;
            client.reactivation = {
              reactivated_at: result?.reactivatedAt,
              reactivated_by: result?.reactivatedBy,
              reactivated_by_name: result?.reactivatedByName,
            };
          }
          this.snackBarService.genericSnackBar(
            `${clientName} has been reactivated. Its patients were not re-enrolled.`,
            ['success-snackbar'],
            6000
          );
          // Only while it is still the selected client. The parent answers this event by re-reading
          // that id and making it the active client, so emitting after a switch would yank the
          // dashboard back to the client the administrator just left.
          if (this.isStillSelected(clientId)) {
            this.clientReactivated.emit(clientId);
          }
        },
        (error) => {
          console.error(error);
          // Same as on start: the backend's refusal (already active, not an administrator) is
          // something the user can act on, so it beats the generic line.
          this.snackBarService.genericSnackBar(
            error?.error?.message ?? `An error occurred while reactivating ${clientName}. The client is still deactivated.`,
            ['error-snackbar'],
            6000
          );
          // The endpoint 409s only when the client is already active — another administrator got
          // there first, or this card was loaded before they did. The state asked for is already true,
          // so the card re-reads it (with the other administrator's stamp) instead of flipping the flag
          // locally, and stops offering a button that can only 409 again.
          if (error?.status === 409 && this.isStillSelected(clientId)) {
            this.clientReactivated.emit(clientId);
          }
        }
      );
  }
}
